// ===========================================
// School-Specific Login Page - Monster Character Design
// ===========================================

import { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/useAuth';
import { TwoFactorRequiredError, User } from '../contexts/AuthContext.types';
import { TwoFactorVerify } from '../components/security/TwoFactorVerify';
import { LOGIN_NOTICE_KEY } from '../components/parent/ConsentGate';
import { AxiosError } from 'axios';
import { getDashboardPath } from '../utils/routing';

export default function SchoolLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolSlug: urlSchoolSlug } = useParams<{ schoolSlug: string }>();
  const { school, schoolSlug, loading: schoolLoading, error: schoolError } = useTenant();
  const { login, completeLogin, isLoading: authLoading, isAuthenticated, user } = useAuth();

  // Where to land after login: the page the user was bounced from (set by
  // ProtectedRoute as location.state.from — e.g. an event deep-link from an
  // email), falling back to the role dashboard.
  const fromPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
  const redirectAfterLogin = (who: Pick<User, 'role' | 'isStaff'>): string =>
    fromPath || getDashboardPath(who.role, urlSchoolSlug || schoolSlug || '', { isStaff: who.isStaff });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2FA state
  const [twoFactorTempToken, setTwoFactorTempToken] = useState<string | null>(null);

  // #442: the notice a consent decline leaves for this page (ConsentGate). Read in the
  // initializer, cleared in an effect: StrictMode runs initializers twice and keeps the second
  // result, so removing it there would lose the notice (#442 /qa L2).
  const [notice] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(LOGIN_NOTICE_KEY);
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.removeItem(LOGIN_NOTICE_KEY);
    } catch {
      // storage blocked: nothing to clear
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const loginUser = await login({
        email: email.trim(),
        password,
        schoolSlug: urlSchoolSlug || undefined,
        rememberMe,
      });
      // Return to the deep-linked page if any, else the role dashboard.
      navigate(redirectAfterLogin(loginUser));
    } catch (err) {
      // Check if 2FA verification is required
      if (err instanceof TwoFactorRequiredError) {
        setTwoFactorTempToken(err.tempToken);
        return;
      }


      const axiosError = err as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || 'Login failed. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };


  // Handle successful 2FA verification
  const handle2FASuccess = async (data: {
    accessToken: string;
    refreshToken: string;
    user: { id: string; email: string; firstName: string; lastName: string; role: string; schoolId: string };
  }) => {
    const loginUser = await completeLogin(data as Parameters<typeof completeLogin>[0]);
    navigate(redirectAfterLogin(loginUser));
  };

  // Handle 2FA cancel - return to login form
  const handle2FACancel = () => {
    setTwoFactorTempToken(null);
  };

  // Loading states
  if (schoolLoading || authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Already signed in? Go to the dashboard instead of showing the form.
  //
  // ⚠️ MUST come after the `authLoading` gate above. Deciding this while auth is
  // still resolving would read `isAuthenticated` as false and render the form to
  // someone who IS logged in — the very bug this fixes — or, worse, bounce a
  // genuinely logged-out user mid-check.
  //
  // `replace` so the login page does not enter history: without it, Back from
  // the dashboard returns here and the loop repeats. Reuses redirectAfterLogin,
  // so a deep-link in `location.state.from` is still honoured — arriving at
  // /login with a `from` and a live session lands on the deep-link, not the
  // dashboard. Andrew, staging 2026-09-08.
  if (isAuthenticated && user && !twoFactorTempToken) {
    return <Navigate to={redirectAfterLogin(user)} replace />;
  }

  // School not found
  if (schoolError || !school) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#FCF6E6',
          px: 2,
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          School Not Found
        </Typography>
        <Typography color="text.secondary" paragraph align="center">
          The school you're looking for doesn't exist or is no longer active.
        </Typography>
        <Button component={RouterLink} to="/schools" variant="contained" sx={{ mt: 2 }}>
          Browse Schools
        </Button>
      </Box>
    );
  }


  // Show 2FA verification screen
  if (twoFactorTempToken) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#FCF6E6',
          px: 2,
        }}
      >
        {school.branding?.logoUrl && (
          <Box
            component="img"
            src={school.branding.logoUrl}
            alt={school.name}
            sx={{ maxWidth: 120, height: 'auto', mb: 3 }}
          />
        )}
        <Box sx={{ '& .MuiCard-root': { border: '2px solid #FFAE9E', bgcolor: 'rgba(255, 174, 158, 0.12)' } }}>
          <TwoFactorVerify
            tempToken={twoFactorTempToken}
            email={email}
            onSuccess={handle2FASuccess}
            onCancel={handle2FACancel}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        bgcolor: '#FCF6E6',
        pt: { xs: '50px', sm: 2 },
        pb: { xs: 3, sm: 2 },
        px: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Music Notes - Left Side */}
      <Box
        sx={{
          position: 'absolute',
          left: { xs: -20, sm: 20, md: 60 },
          top: 0,
          bottom: 0,
          width: { xs: 80, sm: 100, md: 120 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          pointerEvents: 'none',
          opacity: 0.6,
        }}
      >
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%', transform: 'translateY(-30px)' }} />
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%', transform: 'translateY(50px)' }} />
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%', transform: 'translateY(-20px)' }} />
      </Box>

      {/* Music Notes - Right Side */}
      <Box
        sx={{
          position: 'absolute',
          right: { xs: -20, sm: 20, md: 60 },
          top: 0,
          bottom: 0,
          width: { xs: 80, sm: 100, md: 120 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          pointerEvents: 'none',
          opacity: 0.6,
        }}
      >
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%', transform: 'translateY(40px)' }} />
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%', transform: 'translateY(-40px)' }} />
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%', transform: 'translateY(30px)' }} />
      </Box>

      {/* School Logo */}
      {school.branding?.logoUrl ? (
        <Box
          component="img"
          src={school.branding.logoUrl}
          alt={school.name}
          sx={{
            maxWidth: { xs: 100, sm: 120, md: 140 },
            height: 'auto',
            mb: { xs: 1, sm: 2 },
            zIndex: 1,
          }}
        />
      ) : (
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            maxWidth: { xs: 100, sm: 120, md: 140 },
            height: 'auto',
            mb: { xs: 1, sm: 2 },
            zIndex: 1,
          }}
        />
      )}

      {/* #442: why the parent was logged out (a consent decline) */}
      {notice && !error && (
        <Alert
          severity="info"
          sx={{
            width: '100%',
            maxWidth: { xs: 480, sm: 420, md: 480 },
            mb: 1,
            zIndex: 2,
          }}
        >
          {notice}
        </Alert>
      )}

      {/* Error Alert - above monster for full-width visibility */}
      {error && (
        <Alert
          severity="error"
          sx={{
            width: '100%',
            maxWidth: { xs: 480, sm: 420, md: 480 },
            mb: 1,
            zIndex: 2,
            fontWeight: 600,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Monster Character Container */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: { xs: 480, sm: 420, md: 480 },
          zIndex: 1,
        }}
      >
        {/* Monster Image */}
        <Box component="img" src="/monster.png" alt="Login Monster" sx={{ width: '100%', height: 'auto', display: 'block' }} />

        {/* Form Container - Positioned in Monster's Mouth */}
        <Box
          sx={{
            position: 'absolute',
            top: '33%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '48%',
            maxWidth: 234,
          }}
        >

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {/* Login Title */}
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
                color: 'white',
                textAlign: 'center',
                mb: 1,
              }}
            >
              Login
            </Typography>

            <TextField
              fullWidth
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              size="small"
              sx={{
                mb: 1,
                '& .MuiInputBase-root': { bgcolor: 'rgba(255, 255, 255, 0.95)' },
                '& .MuiInputBase-input': { fontSize: '0.85rem', py: 1 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" sx={{ fontSize: '1rem' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              size="small"
              sx={{
                mb: 1,
                '& .MuiInputBase-root': { bgcolor: 'rgba(255, 255, 255, 0.95)' },
                '& .MuiInputBase-input': { fontSize: '0.85rem', py: 1 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" sx={{ fontSize: '1rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOff sx={{ fontSize: '1rem' }} /> : <Visibility sx={{ fontSize: '1rem' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Remember Me Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                  size="small"
                  sx={{ py: 0 }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Remember me
                </Typography>
              }
              sx={{ mb: 0.5, ml: 0 }}
            />

            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={isSubmitting}
              sx={{
                py: 0.5,
                px: 4,
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'block',
                mx: 'auto',
              }}
            >
              {isSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Sign In'}
            </Button>
          </form>
        </Box>
      </Box>

      {/* Footer Links - Below Monster */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Link
          component={RouterLink}
          to={`/${schoolSlug}/forgot-password`}
          underline="hover"
          color="primary"
          sx={{ display: 'block', mb: 0.5, fontSize: '0.875rem' }}
        >
          Forgot Password?
        </Link>
        <Link component={RouterLink} to="/schools" underline="hover" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          Find a Different School
        </Link>
      </Box>

      {/* Meet & Greet Footer */}
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2, fontSize: '0.875rem' }}>
        New to {school.name}?{' '}
        <Link component={RouterLink} to={`/${schoolSlug}/meet-and-greet`} underline="hover" color="primary">
          Book a Meet & Greet
        </Link>
      </Typography>

      {/* Character below footer */}
      <Box
        component="img"
        src="/character.png"
        alt=""
        sx={{
          maxWidth: { xs: 100, sm: 120 },
          height: 'auto',
          mt: 6,
          ml: { xs: 10, sm: 12 },
          opacity: 0.8,
          zIndex: 1,
        }}
      />
    </Box>
  );
}
